import emailjs from 'emailjs-com';

// Konfigurasi EmailJS dari environment variables
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
const EMAILJS_CUSTOMER_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_CUSTOMER_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
const RECIPIENT_EMAIL = import.meta.env.VITE_RECIPIENT_EMAIL;

// Email yang WAJIB selalu menerima salinan setiap inquiry yang masuk,
// apa pun bahasa / metode (email maupun WhatsApp) yang dipilih customer.
// Bisa ditambah lewat env: VITE_INQUIRY_NOTIFY_EMAILS=a@x.com,b@x.com
export const ALWAYS_NOTIFY_EMAILS: string[] = Array.from(new Set(
  [
    'tony@tncakesbali.com',
    'admin@tncakesbali.com',
    'antonio.pati@outlook.com',
    'tncakesdev@gmail.com',
    (import.meta.env.VITE_INQUIRY_NOTIFY_EMAILS as string) || '',
  ].join(',')
    .split(/[,;\s]+/)
    .map(e => e.trim().toLowerCase())
    .filter(Boolean)
));

// Inisialisasi EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

export interface InquiryFormData {
  fullName: string;
  email: string;
  countryCode: string;
  whatsapp: string;
  dateNeeded: string;
  occasion: string;
  theme: string;
  servings: string;
  message: string;
  images?: File[] | null;
}

export const sendInquiryEmail = async (formData: InquiryFormData, customRecipient?: string, language: string = 'en'): Promise<boolean> => {
  try {
    // Validasi konfigurasi
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      console.error('EmailJS belum dikonfigurasi dengan benar. Periksa file .env');
      return false;
    }

    // Format data untuk template email
    const templateParams = {
      to_name: 'TN Cakes Bali® Team', // Nama penerima
      to_email: customRecipient || RECIPIENT_EMAIL, // Email tujuan dari parameter atau environment
      from_name: formData.fullName,
      from_email: formData.email,
      customer_name: formData.fullName,
      customer_email: formData.email,
      customer_whatsapp: `${formData.countryCode} ${formData.whatsapp}`,
      date_needed: formData.dateNeeded,
      occasion: formData.occasion,
      theme: formData.theme || (language === 'en' ? 'Not specified' : 'Tidak disebutkan'),
      servings: formData.servings || (language === 'en' ? 'Not specified' : 'Tidak disebutkan'),
      message: formData.message,
      submission_date: new Date().toLocaleString('id-ID', {
        timeZone: 'Asia/Jakarta',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };

    // Kirim email menggunakan EmailJS
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log('Email berhasil dikirim:', response);
    return true;
  } catch (error) {
    console.error('Gagal mengirim email:', error);
    return false;
  }
};

/**
 * Kirim notifikasi inquiry ke SEMUA penerima: penerima utama (berdasarkan bahasa),
 * ALWAYS_NOTIFY_EMAILS, dan penerima tambahan opsional. Duplikat otomatis dihapus.
 * Mengembalikan true jika minimal satu email berhasil terkirim.
 */
export const sendInquiryEmailToAll = async (
  formData: InquiryFormData,
  primaryRecipient?: string | null, // undefined = pakai VITE_RECIPIENT_EMAIL, null = tanpa penerima utama
  language: string = 'en',
  extraRecipients: string[] = []
): Promise<{ success: boolean; sent: string[]; failed: string[] }> => {
  const primary = primaryRecipient === undefined ? RECIPIENT_EMAIL : primaryRecipient;
  const recipients = Array.from(new Set(
    [primary, ...ALWAYS_NOTIFY_EMAILS, ...extraRecipients]
      .map(e => (e || '').trim().toLowerCase())
      .filter(Boolean)
  ));

  const results = await Promise.all(
    recipients.map(async (to) => ({ to, ok: await sendInquiryEmail(formData, to, language) }))
  );

  const sent = results.filter(r => r.ok).map(r => r.to);
  const failed = results.filter(r => !r.ok).map(r => r.to);
  if (failed.length) console.error('Inquiry email gagal terkirim ke:', failed);
  return { success: sent.length > 0, sent, failed };
};

// Fungsi untuk mengirim email konfirmasi ke customer
export const sendCustomerConfirmationEmail = async (customerEmail: string, customerName: string): Promise<boolean> => {
  try {
    // Skip jika template konfirmasi customer tidak dikonfigurasi
    if (!EMAILJS_CUSTOMER_TEMPLATE_ID) {
      console.log('Template konfirmasi customer tidak dikonfigurasi, melewati pengiriman email konfirmasi');
      return true;
    }

    const templateParams = {
      to_name: customerName,
      to_email: customerEmail,
      from_name: 'TN Cakes Bali®',
      customer_name: customerName,
      reply_to: RECIPIENT_EMAIL
    };

    // Kirim email konfirmasi menggunakan template terpisah
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_CUSTOMER_TEMPLATE_ID,
      templateParams
    );

    console.log('Email konfirmasi berhasil dikirim ke customer:', response);
    return true;
  } catch (error) {
    console.error('Gagal mengirim email konfirmasi ke customer:', error);
    // Jangan gagalkan proses utama jika email konfirmasi gagal
    return true;
  }
};
